import { Component, computed, effect, inject, input, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { firstValueFrom } from "rxjs";
import { AppConfig } from "../../app.config";
import { ProjectService } from "../../shared/services/project.service";
import { ImageLoaderService } from "../../shared/services/image-loader.service";
import { KnowledgeChunk, KnowledgeData, KnowledgeDocAgg } from "../models/knowledge-response-model";

/** Per-image load state, keyed by `image_id`. */
type ImageState =
  | { status: "loading" }
  | { status: "loaded"; src: string }
  | { status: "error" };

/**
 * Renders the Knowledge specialist's structured output (`agent_complete.data`).
 * Shows the resources used to answer the question:
 *  - a row of the referenced figures (image chunks), and
 *  - the source documents (`reference.doc_aggs`) as clickable pills.
 *
 * Documents and images are served through our own backend proxy (which holds the
 * RAGFlow credentials), not RAGFlow directly:
 *  - documents: GET {apiUrl_v2}/xwai/docs/?doc=<id>&dataset=<id>  (file bytes)
 *  - images:    GET {apiUrl_v2}/xwai/docs/?img=<image_id>         (image bytes)
 * Both require the app's auth header, so we fetch with it and use object/data URLs.
 */
@Component({
  selector: "app-xwai-knowledge",
  templateUrl: "./xwai-knowledge.component.html",
  styleUrls: ["./xwai-knowledge.component.less"],
  imports: [CommonModule, NzIconModule],
})
export class XwaiKnowledgeComponent {
  private http = inject(HttpClient);
  private projectService = inject(ProjectService);
  private imageLoader = inject(ImageLoaderService);
  private notification = inject(NzNotificationService);

  readonly data = input.required<KnowledgeData>();

  /** The documents that grounded the answer — one pill each. */
  protected docAggs = computed<KnowledgeDocAgg[]>(
    () => this.data().reference?.doc_aggs ?? [],
  );

  /** Retrieved chunks that carry a renderable figure. */
  protected imageChunks = computed<KnowledgeChunk[]>(() =>
    (this.data().reference?.chunks ?? []).filter(
      (c) => c.doc_type === "image" && !!c.image_id,
    ),
  );

  /**
   * The document-download endpoint needs a `dataset` id, which `doc_aggs` omits.
   * Every chunk carries both, so map `document_id -> dataset_id` from `chunks`.
   */
  private datasetByDoc = computed(() => {
    const map = new Map<string, string>();
    for (const c of this.data().reference?.chunks ?? []) {
      if (c.document_id && c.dataset_id && !map.has(c.document_id)) {
        map.set(c.document_id, c.dataset_id);
      }
    }
    return map;
  });

  /** doc_id currently being fetched, so its pill can show a spinner. */
  protected loadingDocId = signal<string | null>(null);

  /** Loaded/loading state per image chunk, keyed by `image_id`. */
  protected imageStates = signal<Record<string, ImageState>>({});

  /** image_ids we've already kicked off a load for (guards the effect). */
  private requestedImages = new Set<string>();

  constructor() {
    // Load each referenced figure's thumbnail as soon as it appears in the data.
    effect(() => {
      for (const chunk of this.imageChunks()) {
        if (!this.requestedImages.has(chunk.image_id)) {
          this.requestedImages.add(chunk.image_id);
          this.loadImage(chunk.image_id);
        }
      }
    });
  }

  private imageUrl(imageId: string): string {
    return `${AppConfig.settings.apiUrl_v2}/xwai/docs/?img=${encodeURIComponent(imageId)}`;
  }

  private loadImage(imageId: string): void {
    this.imageStates.update((s) => ({ ...s, [imageId]: { status: "loading" } }));
    this.imageLoader.getImageBase64(this.imageUrl(imageId)).subscribe({
      next: (src: string) =>
        this.imageStates.update((s) => ({ ...s, [imageId]: { status: "loaded", src } })),
      error: (err) => {
        console.error("Failed to load knowledge image:", err);
        this.imageStates.update((s) => ({ ...s, [imageId]: { status: "error" } }));
      },
    });
  }

  /** Open a referenced figure in a new tab (fetched as a blob, behind auth). */
  async openImage(imageId: string): Promise<void> {
    const tab = window.open("about:blank", "_blank");
    try {
      const blob = await firstValueFrom(this.imageLoader.getImage(this.imageUrl(imageId)));
      const objectUrl = URL.createObjectURL(blob);
      if (tab) tab.location.href = objectUrl;
      else window.open(objectUrl, "_blank"); // fallback if the tab was blocked
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      console.error("Failed to open knowledge image:", err);
      tab?.close();
      this.notification.error("Error", "Failed to open image.");
    }
  }

  /** GET {apiUrl_v2}/xwai/docs/?doc=<id>&dataset=<id> → open the file in a new tab. */
  async openDocument(doc: KnowledgeDocAgg): Promise<void> {
    if (this.loadingDocId()) return;

    const datasetId = this.datasetByDoc().get(doc.doc_id);
    if (!datasetId) {
      this.notification.error("Error", "Could not resolve the document's dataset.");
      return;
    }

    // Open the tab synchronously (within the click gesture) so popup blockers
    // don't kill it; navigate it to the blob once the bytes arrive. NOTE: no
    // "noopener" here — that flag makes window.open return null, losing the handle.
    const tab = window.open("about:blank", "_blank");

    this.loadingDocId.set(doc.doc_id);
    try {
      const params = new URLSearchParams({ doc: doc.doc_id, dataset: datasetId });
      const url = `${AppConfig.settings.apiUrl_v2}/xwai/docs/?${params.toString()}`;
      const blob = await firstValueFrom(
        this.http.get(url, {
          headers: this.projectService.getAuthorizationHeader(),
          responseType: "blob",
        }),
      );
      const objectUrl = URL.createObjectURL(blob);
      if (tab) tab.location.href = objectUrl;
      else window.open(objectUrl, "_blank"); // fallback if the tab was blocked
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      console.error("Failed to open knowledge document:", err);
      tab?.close();
      this.notification.error("Error", `Failed to open "${doc.doc_name}".`);
    } finally {
      this.loadingDocId.set(null);
    }
  }
}
